import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lightweight, decoupled attention-guide system.
 *
 * Any component can call `triggerAttention('some-id')` to request a
 * visual highlight on a target element. The target registers itself
 * with `useAttentionTarget('some-id')` and receives an animated
 * highlight state it can use however it likes.
 *
 * If the target hasn't mounted yet when `triggerAttention` is called,
 * the request is queued and delivered as soon as the target registers.
 */

const EVENT_NAME = 'attention-guide';
const PENDING_TTL = 5000;

interface AttentionEvent {
  targetId: string;
}

const pendingQueue = new Map<string, number>();

export function triggerAttention(targetId: string) {
  pendingQueue.set(targetId, Date.now());
  window.dispatchEvent(
    new CustomEvent<AttentionEvent>(EVENT_NAME, { detail: { targetId } }),
  );
}

interface UseAttentionTargetOptions {
  /** How long the highlight stays active (ms). Default 2000. */
  duration?: number;
  /** Scroll the element into view when highlighted. Default true. */
  scrollIntoView?: boolean;
}

/**
 * Register an element as an attention target.
 *
 * Returns a ref to attach to the DOM element and an `isHighlighted`
 * boolean that stays true for `duration` ms after being triggered.
 *
 * @example
 * const { ref, isHighlighted } = useAttentionTarget('shopify-install');
 * <Button ref={ref} className={isHighlighted ? 'ring-2 ring-primary animate-pulse' : ''}>
 */
export function useAttentionTarget<T extends HTMLElement = HTMLElement>(
  targetId: string,
  options: UseAttentionTargetOptions = {},
) {
  const { duration = 2000, scrollIntoView = true } = options;
  const ref = useRef<T>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const activate = useCallback(() => {
    clearTimeout(timerRef.current);
    setIsHighlighted(true);

    if (scrollIntoView && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    timerRef.current = setTimeout(() => setIsHighlighted(false), duration);
  }, [duration, scrollIntoView]);

  const handleEvent = useCallback(
    (e: Event) => {
      const detail = (e as CustomEvent<AttentionEvent>).detail;
      if (detail.targetId !== targetId) return;
      pendingQueue.delete(targetId);
      activate();
    },
    [targetId, activate],
  );

  useEffect(() => {
    window.addEventListener(EVENT_NAME, handleEvent);

    // Poll for queued attention that fired before this target mounted.
    // Retries handle React render timing where the component mounts
    // across multiple frames.
    let pollCount = 0;
    const maxPolls = 10;
    const pollInterval = 100;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const checkQueue = () => {
      const pendingTime = pendingQueue.get(targetId);
      if (pendingTime && Date.now() - pendingTime < PENDING_TTL) {
        pendingQueue.delete(targetId);
        activate();
        return;
      }
      pollCount++;
      if (pollCount < maxPolls) {
        pollTimer = setTimeout(checkQueue, pollInterval);
      }
    };
    checkQueue();

    return () => {
      window.removeEventListener(EVENT_NAME, handleEvent);
      clearTimeout(timerRef.current);
      clearTimeout(pollTimer);
    };
  }, [handleEvent, targetId, activate]);

  return { ref, isHighlighted };
}
